import { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Settings, Save, AlertCircle, FileText, FileDown } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

function ContabilidadConfig({ user }) {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await axiosInstance.get('/contabilidad/config');
      setConfig(response.data);
    } catch (error) {
      console.error('Error cargando configuración:', error);
      toast.error('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.post('/contabilidad/config', config);
      toast.success('Configuración guardada correctamente');
    } catch (error) {
      console.error('Error guardando configuración:', error);
      toast.error('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Cargando configuración...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-6 max-w-4xl">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Settings className="h-8 w-8 text-blue-600" />
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900">
                Contabilidad C10
              </h1>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => navigate('/contabilidad/fiscal')}
                variant="outline"
                size="sm"
              >
                Dashboard
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/asientos')}
                variant="outline"
                size="sm"
              >
                Asientos
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/facturas-recibidas')}
                variant="outline"
                size="sm"
              >
                Fact. Recibidas
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/libros')}
                variant="outline"
                size="sm"
              >
                Libros
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/ocr')}
                variant="outline"
                size="sm"
              >
                OCR
              </Button>
            </div>
          </div>
          <p className="text-base text-gray-600">
            Configuración - Parámetros del módulo de contabilidad
          </p>
        </div>

        <Alert className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Esta configuración permite generar asientos contables automáticamente cuando se aprueban facturas.
            Asegúrate de configurar correctamente las cuentas según tu Plan General Contable.
          </AlertDescription>
        </Alert>

        <div className="space-y-6">
          {/* Configuración General */}
          <Card>
            <CardHeader>
              <CardTitle>Configuración General</CardTitle>
              <CardDescription>
                Configura los parámetros generales del módulo contable
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Generar asientos automáticamente</Label>
                  <div className="text-sm text-gray-500">
                    Al aprobar una factura, se generará automáticamente el asiento contable
                  </div>
                </div>
                <Switch
                  checked={config?.auto_generar_asientos || false}
                  onCheckedChange={(checked) => handleChange('auto_generar_asientos', checked)}
                />
              </div>

              <div>
                <Label htmlFor="actividad">Actividad económica</Label>
                <Input
                  id="actividad"
                  value={config?.actividad || ''}
                  onChange={(e) => handleChange('actividad', e.target.value)}
                  placeholder="Ej: Servicios profesionales, Comercio minorista..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Configuración Fiscal */}
          <Card>
            <CardHeader>
              <CardTitle>Configuración Fiscal</CardTitle>
              <CardDescription>
                Parámetros fiscales para cálculos automáticos
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="regimen_iva">Régimen de IVA</Label>
                <select
                  id="regimen_iva"
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={config?.regimen_iva || 'general'}
                  onChange={(e) => handleChange('regimen_iva', e.target.value)}
                >
                  <option value="general">General</option>
                  <option value="simplificado">Simplificado</option>
                  <option value="recargo_equivalencia">Recargo de Equivalencia</option>
                </select>
              </div>

              <div>
                <Label htmlFor="tipo_irpf">Tipo de retención IRPF (%)</Label>
                <Input
                  id="tipo_irpf"
                  type="number"
                  step="0.01"
                  value={config?.tipo_irpf || 0.15}
                  onChange={(e) => handleChange('tipo_irpf', parseFloat(e.target.value))}
                  placeholder="0.15"
                />
                <div className="text-sm text-gray-500 mt-1">
                  Porcentaje decimal (ej: 0.15 = 15%)
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cuentas Contables */}
          <Card>
            <CardHeader>
              <CardTitle>Cuentas Contables por Defecto</CardTitle>
              <CardDescription>
                Configuración de cuentas para asientos automáticos (Plan General Contable)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="cuenta_ventas_defecto">Cuenta de Ventas/Ingresos</Label>
                <Input
                  id="cuenta_ventas_defecto"
                  value={config?.cuenta_ventas_defecto || '705'}
                  onChange={(e) => handleChange('cuenta_ventas_defecto', e.target.value)}
                  placeholder="705"
                />
                <div className="text-sm text-gray-500 mt-1">
                  Predeterminado: 705 - Prestaciones de servicios
                </div>
              </div>

              <div>
                <Label htmlFor="cuenta_clientes">Cuenta de Clientes</Label>
                <Input
                  id="cuenta_clientes"
                  value={config?.cuenta_clientes || '430'}
                  onChange={(e) => handleChange('cuenta_clientes', e.target.value)}
                  placeholder="430"
                />
                <div className="text-sm text-gray-500 mt-1">
                  Predeterminado: 430 - Clientes
                </div>
              </div>

              <div>
                <Label htmlFor="cuenta_iva_repercutido">Cuenta de IVA Repercutido</Label>
                <Input
                  id="cuenta_iva_repercutido"
                  value={config?.cuenta_iva_repercutido || '477'}
                  onChange={(e) => handleChange('cuenta_iva_repercutido', e.target.value)}
                  placeholder="477"
                />
                <div className="text-sm text-gray-500 mt-1">
                  Predeterminado: 477 - Hacienda Pública, IVA repercutido
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Botón Guardar */}
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Guardando...' : 'Guardar Configuración'}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default ContabilidadConfig;
